import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { Button } from "react-bootstrap";
import { fetchLeads } from "../../Redux/Reducers/LeadsReducer";
import PageHeader1 from "../../components/common/PageHeader1";
import { Select, MenuItem } from "@mui/material";
import { usePermissions } from "../../context/AuthContext";

const VALID_STATUSES = ["Pending", "Contacted"];

function LeadsList() {
  const { can } = usePermissions();
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [""],
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const leads = useSelector((state) => state.LeadsReducer.leads);
  const loading = useSelector((state => state?.LeadsReducer?.loading));

  const pagination = useSelector((state) => state?.LeadsReducer?.pagination);

  useEffect(() => {
    const { page, pageSize } = paginationModel;
    dispatch(fetchLeads({ page: page + 1, limit: pageSize }));
  }, [paginationModel, dispatch]);

  const rows = Array.isArray(leads) ? leads?.map((item, index) => {
    return {
      id: paginationModel.page * paginationModel.pageSize + index + 1,
      name: item?.name ?? '-',
      mobile: item?.mobile ?? '-',
      email: item?.email ?? '-',
      _id: item?._id,
      status: item?.status ?? 'Pending',
      createdAt: item?.createdAt
        ? new Date(item?.createdAt).toLocaleString()
        : "",
    };
  }) : [];

  function deleteLead(id) {
    const token = localStorage.getItem("vedhvidhi-admin-token");

    axios.delete(`${process.env.REACT_APP_BASEURL}/user/leads/${id}`, {
      headers: {
        Authorization: token,
      }
    })
      .then(response => {
        if (response?.data?.success) {
          dispatch(fetchLeads({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
          }));
        }
      })
      .catch(error => {
        console.error(error);
      });
  }

  const handleClickOpen = (id) => {
    setSelectedUserId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const handleDelete = () => {
    if (selectedUserId) {
      deleteLead(selectedUserId);
    }
    handleClose();
  };

  const handleStatusChange = async (id, newStatus) => {
    const token = localStorage.getItem("vedhvidhi-admin-token");
    try {
      await axios.patch(
        `${process.env.REACT_APP_BASEURL}/admin/lead/${id}`,
        { status: newStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      dispatch(fetchLeads({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      }));
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const columns = [
    { field: "id", headerName: "S.No", width: 150 },
    { field: "name", headerName: "Full Name", width: 180 },
    { field: "mobile", headerName: "Mobile No.", width: 180 },
    { field: "email", headerName: "Email", width: 250 },
    { field: "createdAt", headerName: "Created At", width: 200 },
    {
      field: "status",
      headerName: "Status",
      width: 250,
      renderCell: ({ row }) => {
        const displayValue = VALID_STATUSES.includes(row.status) ? row.status : "Pending";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Select
              sx={{ width: 150 }}
              size="small"
              value={displayValue}
              onChange={(e) => handleStatusChange(row._id, e.target.value)}
              disabled={!can('lead_management:edit')}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Contacted">Contacted</MenuItem>
            </Select>
          </div>
        );
      },
    },
    ...(can('lead_management:delete') ? [{
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: ({ row }) => (
        <div>
          {can('lead_management:delete') && (
            <Tooltip title="Delete Lead">
              <IconButton onClick={() => handleClickOpen(row._id)}>
                <i className="icofont-ui-delete text-danger"></i>
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <PageHeader1 pagetitle='Lead Management List' />
              <div className="card-body">
                <div
                  id="myDataTable_wrapper"
                  className="dataTables_wrapper dt-bootstrap5 no-footer"
                >
                  <div className="row">
                    <div className="col-sm-12">
                      <Box sx={{ width: 1 }}>
                        <Box sx={{ height: 400, width: '100%' }}>
                          {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <CircularProgress />
                            </Box>
                          ) : (
                            <DataGrid
                              columns={columns}
                              rows={rows}
                              getRowId={(row) => row._id}
                              disableColumnFilter
                              disableDensitySelector
                              slots={{ toolbar: GridToolbar }}
                              filterModel={filterModel}
                              onFilterModelChange={(newModel) =>
                                setFilterModel(newModel)
                              }
                              slotProps={{ toolbar: { showQuickFilter: true } }}
                              columnVisibilityModel={columnVisibilityModel}
                              onColumnVisibilityModelChange={(newModel) =>
                                setColumnVisibilityModel(newModel)
                              }
                              paginationMode="server"
                              rowCount={pagination?.totalDocs || 0}
                              pageSizeOptions={[10, 25, 50, 100]}
                              paginationModel={paginationModel}
                              onPaginationModelChange={setPaginationModel}
                              disableRowSelectionOnClick
                            />
                          )}
                        </Box>
                      </Box>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">{"Delete Confirmation"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this record?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleDelete} variant="danger" autoFocus>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default LeadsList;